import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import LandingPage from "./features/landing/LandingPage";
import AuthPage from "./features/auth/AuthPage";
import DiscordFinishPage from "./features/auth/DiscordFinishPage";
import DashboardPage from "./features/dashboard/DashboardPage";
import SettingsPage from "./features/settings/SettingsPage";
import FriendsPage from "./features/friends/FriendsPage";
import AvailabilityPage from "./features/availability/AvailabilityPage";
import SchedulerPage from "./features/scheduler/SchedulerPage";
import CreateSchedulerPage from "./features/scheduler/CreateSchedulerPage";
import PricingPage from "./features/pricing/PricingPage";
import PrivacyPage from "./features/legal/PrivacyPage";
import TermsPage from "./features/legal/TermsPage";
import DiscordBotPage from "./features/discord/DiscordBotPage";
import PublicProfilePage from "./features/profile/PublicProfilePage";
import ProtectedRoute from "./app/ProtectedRoute";
import GuestRoute from "./app/GuestRoute";
import GuestLayout from "./app/GuestLayout";
import { useAuth } from "./app/useAuth";
import AppLayout from "./app/AppLayout";
import { useTheme } from "./app/useTheme";

// C4 — the link-shared scheduler route renders inside GuestRoute (which
// guarantees SOME user exists: silent anonymous sign-in for logged-out
// visitors, pass-through for real users). The shell is then chosen by
// `user.isAnonymous`: a real logged-in user keeps the full AppLayout (nav,
// notifications, account menu); only anonymous guests get the stripped
// GuestLayout. This prevents the regression where wrapping every visitor in
// GuestLayout would strip the normal app shell from a real user who opens a
// shared link. A guest who converts mid-session immediately gets AppLayout.
function SchedulerShell({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user?.isAnonymous) {
    return <GuestLayout onSignIn={() => navigate("/auth")}>{children}</GuestLayout>;
  }
  return <AppLayout>{children}</AppLayout>;
}

function RedirectWhenSignedIn({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // An anonymous guest (link-shared scheduler voter) is NOT a full account —
    // don't bounce them away from /auth if they navigate there to sign up. Only
    // a real signed-in user is redirected to their destination.
    if (!loading && user && !user.isAnonymous) {
      const redirectPath = localStorage.getItem("postLoginRedirect");
      if (redirectPath) {
        localStorage.removeItem("postLoginRedirect");
        const [pathname, search] = redirectPath.split("?");
        const isPollRoute = /^\/scheduler\/[^/]+$/.test(pathname);
        const isGroupPollRoute = /^\/groups\/[^/]+\/polls\/[^/]+$/.test(pathname);
        const searchParams = new URLSearchParams(search || "");
        const isFriendRequestRoute = pathname === "/friends" && searchParams.has("request");
        const isFriendInviteRoute = pathname === "/friends" && searchParams.has("invite");
        if (isPollRoute || isGroupPollRoute || isFriendRequestRoute || isFriendInviteRoute) {
          navigate(redirectPath, { replace: true });
          return;
        }
      }
      navigate("/dashboard", { replace: true });
    }
  }, [loading, navigate, user]);

  return children;
}

function GroupPollRouteRedirect() {
  const { groupId, pollId } = useParams();
  const params = new URLSearchParams();
  if (groupId) params.set("groupPollGroupId", groupId);
  if (pollId) params.set("groupPollId", pollId);
  return <Navigate to={`/dashboard?${params.toString()}`} replace />;
}

export default function App() {
  const { darkMode } = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  const isPublicRoute = ["/", "/auth", "/pricing", "/privacy", "/terms", "/discord-bot"].includes(
    location.pathname
  );
  const forceDarkTheme = !user && isPublicRoute;

  return (
    <>
      <Toaster
        theme={forceDarkTheme || darkMode ? "dark" : "light"}
        position="top-right"
        toastOptions={{
          className: "font-sans",
          duration: 4000,
        }}
      />
      <Routes location={location} key={location.pathname}>
      <Route
        path="/"
        element={
          <RedirectWhenSignedIn>
            <LandingPage />
          </RedirectWhenSignedIn>
        }
      />
      <Route
        path="/auth"
        element={
          <RedirectWhenSignedIn>
            <AuthPage />
          </RedirectWhenSignedIn>
        }
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/auth/discord/finish" element={<DiscordFinishPage />} />
      <Route path="/discord-bot" element={<DiscordBotPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FriendsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/availability"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AvailabilityPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId/polls/:pollId"
        element={
          <ProtectedRoute>
            <GroupPollRouteRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheduler/:id"
        element={
          <GuestRoute>
            <SchedulerShell>
              <SchedulerPage />
            </SchedulerShell>
          </GuestRoute>
        }
      />
      <Route
        path="/scheduler/:id/edit"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CreateSchedulerPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:identifier"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PublicProfilePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CreateSchedulerPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
